import Backbone from 'backbone';
import $ from 'jquery';
import ko from 'knockout';
import _ from 'underscore';
import adCustomizersPickerTemplate from '@bingads-webui-campaign-legacy/crud-ad/template/adsEditor/adCustomizersPicker.pug';

class AdCustomizersPicker extends Backbone.View {
  initialize({
    target,
    options}) {

    this.target = target;
    this.items = ko.observableArray();
    this.populate(options);

    return this;
  }

  render() {
    let $target = $(this.target);
    let position = $target.offset();

    position.top = position.top + $target.outerHeight();

    this.$el.html(adCustomizersPickerTemplate({
      items: this.items,
      position,
    }));

    // Clicking on elements having this class won't hide currently visible helper
    this.$el.addClass('ac-helper-area open');

    ko.applyBindings({ items: this.items }, this.el);

    return this;
  }

  /**
   * Populates the picker
   * @param {object} options - contains item models and text replacement properties
   * @returns {undefined}
   */
  populate(options) {
    let target = this.target;

    // Clicking on a picker item will replace a portion of the text in the attached text field
    _.each(options.items, (item) => {
      item.action = () => {
        let beginning = target.value.substring(0, options.startIndex);
        let end = target.value.substring(options.endIndex);

        options.updateAction(beginning + item.insert + end);
        let cursorPosition = beginning.length + item.cursorPosition;

        target.setSelectionRange(cursorPosition, cursorPosition);
        target.isSelectedByUser = true;
        // focus itself will trigger the selection event
        target.focus();
        //target.click();

        hide(); // eslint-disable-line no-use-before-define
      };
    });

    this.items(options.items);
    this.activateItemAt(undefined);
  }

  /**
   * Highlights item at given index
   * @param {integer} index - item index
   * @returns {undefined}
   */
  activateItemAt(index) {
    if (this.activeItemIndex !== undefined) {
      this.$el.find(`li:eq(${this.activeItemIndex})`).removeClass('active');
    }

    this.activeItemIndex = index;

    if (this.activeItemIndex !== undefined) {
      this.$el.find(`li:eq(${this.activeItemIndex})`).addClass('active');
    }
  }

  /**
   * Highlights the item above currently highlighted one. If none highlighted or first item is highlighted, highlights the last item.
   * @returns {undefined}
   */
  activatePrevItem() {
    if (this.activeItemIndex === undefined) {
      this.activateItemAt(this.items().length - 1);
    } else {
      this.activateItemAt((this.items().length + this.activeItemIndex - 1) % this.items().length);
    }
  }

  /**
   * Highlights the item below currently highlighted one. If none highlighted or last item is highlighted, highlights the first item.
   * @returns {undefined}
   */
  activateNextItem() {
    if (this.activeItemIndex === undefined) {
      this.activateItemAt(0);
    } else {
      this.activateItemAt((this.activeItemIndex + 1) % this.items().length);
    }
  }

  /**
   * Executes action for currently selected item.
   * @returns {undefined}
   */
  pickActiveItem() {
    if (this.activeItemIndex !== undefined) {
      this.items()[this.activeItemIndex].action();
    }
  }
}

/*
 * Picker instance (used by hide() and show() to update, register/deregister events and change visibility)
 */
let picker = null;

/**
 * Hides picker when user clicks outside of ad customizer helper areas
 * @param {object} event - click event
 * @returns {undefined}
 */
function clickedAway(event) {
  let target = $(event.target);

  if (target.closest('.ac-helper-area').length === 0) {
    hide(); // eslint-disable-line no-use-before-define
  }
}

/**
 * Handles key down events.
 * Either prevents default event from happening to keep current cursor position
 * or hides the picker.
 * @param {object} event - click event
 * @returns {undefined}
 */
function keydown(event) {
  switch (event.key) {
  case 'ArrowDown':
  case 'ArrowUp':
  case 'Enter':
    event.preventDefault();
    break;
  case 'Escape':
    hide(); // eslint-disable-line no-use-before-define
    break;
  default:
    break;
  }
}

/**
 * Handles key down events.
 * Either updates picker selection or executes selected item's action.
 * @param {object} event - click event
 * @returns {undefined}
 */
function keyup(event) {
  switch (event.key) {
  case 'ArrowDown':
    event.preventDefault();
    event.data.picker.activateNextItem();
    break;
  case 'ArrowUp':
    event.preventDefault();
    event.data.picker.activatePrevItem();
    break;
  case 'Enter':
    event.preventDefault();
    event.data.picker.pickActiveItem();
    break;
  default:
    break;
  }
}

/**
 * Hides picker and deregisters key and click events.
 * @returns {undefined}
 */
export function hide() {
  if (picker) {
    $(picker.target).off('keyup', keyup);
    $(picker.target).off('keydown', keydown);
    $(window).off('click', clickedAway);
    picker.remove();
    picker = null;
  }
}

/**
 * Shows picker and registers for events.
 * @param {object} target - element the picker will be attached to.
 * @param {object} options - picker item models and cursor position info
 * @param {string} key - name of the key that triggered this call
 * @returns {undefined}
 */
export function show(target, options, key) {
  // Update instead of re-render if target hasn't changed
  if (picker && picker.target === target) {
    // Ignore if keys are used to navigate picker items or select an item
    if (key === undefined || _.indexOf(['ArrowDown', 'ArrowUp', 'Enter'], key) === -1) {
      picker.populate(options);
    }
    return;
  }

  hide();

  picker = new AdCustomizersPicker({ target, options });
  $('body').append(picker.render().el);
  $(window).on('click', clickedAway);
  $(target).on('keydown', keydown);
  $(target).on('keyup', { picker }, keyup);
}

/**
 * Checks whether ad customizer picker is visible
 * @returns {bool} true if visible
 */
export function isVisible() {
  return picker !== null;
}
